import cx from 'classnames'
import { type ColumnId } from '@/features/admin/staffing/components/StaffingTable/staffing-table-columns'
import { type WorkspacePostingStatusVariant } from '@/features/admin/staffing/workspace-posting-formatters'
import { Icon } from '@/components/Icon'
import {
  ClickableCheckboxCell,
  ClickableCheckCell,
  ClickableImageCell,
  ClickableTableCell,
  ClickableTableRow,
  ClickableTextCell,
} from '@/components/Table/ClickableTable'
import { type Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import styles from './StaffingTableRow.module.css'

export interface StaffingTableRowProps {
  columnIds: ColumnId[]
  id: Id
  onPress: () => void
  isSelected: boolean
  onToggleSelected: () => void
  workspaceName: string
  workspaceIcon: string
  opportunityTypeTitle: string
  location: string
  primarySoftware: string
  backupFileNeeded: boolean
  supplementalSoftware: string
  specialtiesText: string
  typeOfWorkText: string
  capacity: number
  maxAllowedWorkspaces: number
  displayStatus: string
  statusVariant: WorkspacePostingStatusVariant
}

export function StaffingTableRow({
  columnIds,
  id,
  onPress,
  isSelected,
  onToggleSelected,
  workspaceName,
  workspaceIcon,
  opportunityTypeTitle,
  location,
  primarySoftware,
  backupFileNeeded,
  supplementalSoftware,
  specialtiesText,
  typeOfWorkText,
  capacity,
  maxAllowedWorkspaces,
  displayStatus,
  statusVariant,
}: StaffingTableRowProps) {
  return (
    <ClickableTableRow
      className={styles.root}
      onClick={onPress}
      data-test={`staffing-table-row-${id}`}
    >
      {columnIds.map((columnId) => {
        switch (columnId) {
          case 'Checkbox':
            return (
              <ClickableCheckboxCell
                key={columnId}
                checked={isSelected}
                onChange={onToggleSelected}
                testId={`staffing-table-select-${id}`}
              />
            )
          case 'Workspace':
            return (
              <ClickableImageCell
                key={columnId}
                title={workspaceName}
                image={workspaceIcon}
              />
            )
          case 'OpportunityType':
            return (
              <ClickableTextCell key={columnId}>
                {opportunityTypeTitle}
              </ClickableTextCell>
            )
          case 'Location':
            return (
              <ClickableTextCell key={columnId}>{location}</ClickableTextCell>
            )
          case 'PrimarySoftware':
            return (
              <ClickableTextCell key={columnId}>
                {primarySoftware}
              </ClickableTextCell>
            )
          case 'Backup':
            return (
              <ClickableCheckCell
                key={columnId}
                color={'var(--blue400)'}
                checked={backupFileNeeded}
                alignment="center"
              />
            )
          case 'SupplementalSoftware':
            return (
              <ClickableTextCell key={columnId}>
                {supplementalSoftware}
              </ClickableTextCell>
            )
          case 'Specialties':
            return (
              <ClickableTextCell key={columnId}>
                {specialtiesText}
              </ClickableTextCell>
            )
          case 'TypeOfWork':
            return (
              <ClickableTextCell key={columnId}>
                {typeOfWorkText}
              </ClickableTextCell>
            )
          case 'ProsRequired':
            return (
              <ClickableTextCell key={columnId} alignment="center">
                {capacity}
              </ClickableTextCell>
            )
          case 'MaxConcurrentWorkspaces':
            return (
              <ClickableTextCell key={columnId} alignment="center">
                {maxAllowedWorkspaces}
              </ClickableTextCell>
            )
          case 'Status':
            return (
              <StatusCell
                key={columnId}
                displayStatus={displayStatus}
                variant={statusVariant}
              />
            )
          case 'Action':
            return <ArrowCell key={columnId} />
        }
      })}
    </ClickableTableRow>
  )
}

interface StatusCellProps {
  displayStatus: string
  variant: WorkspacePostingStatusVariant
}

function StatusCell({ displayStatus, variant }: StatusCellProps) {
  return (
    <ClickableTableCell
      className={cx(
        styles.statusCell,
        match(variant, {
          Active: () => styles.statusActive,
          Closed: () => styles.statusClosed,
          Draft: () => styles.statusDraft,
          InReview: () => styles.statusInReview,
          Unspecified: () => undefined,
        }),
      )}
    >
      <span>{displayStatus}</span>
    </ClickableTableCell>
  )
}

function ArrowCell() {
  return (
    <ClickableTableCell>
      <div className={styles.iconContainer}>
        <Icon name="arrow" size={24} />
      </div>
    </ClickableTableCell>
  )
}
